import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useLocation } from 'react-router-dom';

import ItemCardBidButton from '@/components/GridItemCard/Card/components/ItemCardBidButton/ItemCardLiveBidButton';
import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import ItemCardPriceRowExperiment from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRowExperiment';
import { TimedPlusStatus } from '@/components/ItemBidding/components/Estimate/components/Estimate-12hCountdownTimer/components/TimedPlusCountdown/types/TimedPlusStatus.enum';
import PriceRowExperiment from '@/components/ItemCard/PriceRowExperiment';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getIsItemSummaryTimedPlusBiddingOpen,
    getItemSummaryBidCount,
    getItemSummaryCatalogId,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRegisterForAuctionModal } from '@/redux/modules/modal';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getIsSearchPage } from '@/utils/urls';

export type RegisterLinkOrPriceExperimentProps = {
    itemId: number;
    linkHover?: boolean;
    onNavigate?: () => void;
    onRegisterForAuctionClicked?: () => void;
};

//Experiment SEAR-769: New similar items CTA experiment
const RegisterLinkOrPriceExperiment = ({
    itemId,
    linkHover,
    onNavigate,
    onRegisterForAuctionClicked,
}: RegisterLinkOrPriceExperimentProps) => {
    const { pathname } = useLocation();
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const approvedWithBidLimit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const isLargeScreen = useIsWiderThan(mergedCaterwaulAndHammerUIThemes.breakpoints.mobileWidthNum);

    const isHiddenMenuPage = pathname.includes('/catalog/') || pathname.includes('/item/');
    const isHiddenButtonPage = pathname.includes('/c/') || pathname.includes('/item/') || pathname.includes('/search/');

    const isTimedPlusBiddingOpen = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingOpen(state, itemId));

    const isOnSearchPage = getIsSearchPage(pathname);

    const registerForAuctionLabel = formatMessage(
        'itemPage',
        'biddingInfo',
        'timedPlus',
        TimedPlusStatus.NotStarted,
        'registerForAuction'
    );

    let content: JSX.Element;

    if (isSold) {
        content = isLoggedIn ? (
            <div className="flex justify-between [grid-area:price]">
                <ItemCardPriceRowExperiment
                    isHovered={linkHover}
                    itemId={itemId}
                    onNavigate={() => onNavigate?.()}
                    showBidCount={Boolean(bidCount)}
                />
            </div>
        ) : (
            <ItemPriceGatewayContainer
                className="[&_button]:text-link text-base [&:hover_button]:underline [&_button]:no-underline"
                hideIcon
                itemId={itemId}
            />
        );
    } else {
        const isOpenItem = !isPassed && !isPaused && isTimedPlusBiddingOpen;
        const isApprovedForOpenItem = isOpenItem && (approved || approvedWithBidLimit);
        const needsToRegister = !isPassed && !registered && !isHiddenMenuPage;

        let userActionButtonOrLink: JSX.Element | undefined;
        if (isApprovedForOpenItem) {
            userActionButtonOrLink = <ItemCardBidButton itemId={itemId} />;
        } else if (needsToRegister) {
            userActionButtonOrLink = isLargeScreen ? (
                <Button
                    className="w-full"
                    onClick={() => {
                        onRegisterForAuctionClicked?.();
                        dispatch(
                            openRegisterForAuctionModal({
                                catalogId,
                                itemId,
                            })
                        );
                    }}
                    primary={!isOpenItem}
                    red={isOpenItem}
                    small
                >
                    {registerForAuctionLabel}
                </Button>
            ) : (
                <ActionText
                    onClick={() => {
                        dispatch(
                            openRegisterForAuctionModal({
                                catalogId,
                                itemId,
                            })
                        );
                    }}
                >
                    {registerForAuctionLabel}
                </ActionText>
            );
        }
        content = (
            <div className="flex flex-col">
                <ItemCardEstimate
                    className={cn('text-xs smMin:text-sm')}
                    itemId={itemId}
                />
                <div className="flex justify-between [grid-area:price]">
                    <PriceRowExperiment
                        className="mt-0"
                        isCatalogCard
                        itemId={itemId}
                        onNavigate={() => onNavigate?.()}
                    />
                </div>
                {!isHiddenButtonPage && !isOnSearchPage && (
                    <div className="flex h-full items-end">{userActionButtonOrLink}</div>
                )}
            </div>
        );
    }

    return <div className="relative h-full [grid-area:registerlinkorprice]">{content}</div>;
};

export default RegisterLinkOrPriceExperiment;
